// frontend/src/components/inventaire/ClientDetails.jsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  CreditCard as CreditIcon,
  ShoppingCart as CartIcon,
  Star as StarIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../../src/components/utils/formatters';

const ClientDetails = ({ client, onEdit, onPayment }) => {
  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informations personnelles
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Téléphone" 
                  secondary={client.telephone || 'Non renseigné'} 
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Email" 
                  secondary={client.email || 'Non renseigné'} 
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <LocationIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Adresse" 
                  secondary={client.adresse || 'Non renseignée'} 
                />
              </ListItem>
            </List>
            
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={client.est_actif ? <CheckCircleIcon /> : <CancelIcon />}
                label={client.est_actif ? 'Client actif' : 'Client inactif'}
                color={client.est_actif ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informations financières
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total achats
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      {formatCurrency(client.total_achats || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Points fidélité
                    </Typography>
                    <Typography variant="h5" color="warning.main">
                      {client.points_fidelite || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: 'warning.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Crédit client
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">
                          Limite
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(client.limite_credit)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">
                          Solde dû
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          {formatCurrency(client.solde_credit)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">
                          Disponible
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {formatCurrency(client.credit_disponible)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {client.notes && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {client.notes}
              </Typography>
            </Paper>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            Dernière modification: {formatDate(client.date_modification)}
          </Typography>
        </Grid>
      </Grid>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        {client.solde_credit > 0 && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<PaymentIcon />}
            onClick={onPayment}
          >
            Enregistrer un paiement
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onEdit}
        >
          Modifier
        </Button>
      </Box>
    </Box>
  );
};

export default ClientDetails;